/**
 * ComposerPlusMenu — the composer's left-side "+" popover (extensible actions /
 * settings). Each surface declares which options it offers; this renders them.
 *
 * Mixed model: every option declares its own ``type``:
 *   - ``toggle``  → an inline switch (e.g. Network Workbench "Skills" on/off).
 *   - ``action``  → a clickable row that does something (e.g. "Attachment" opens
 *                    the file picker), then closes the menu.
 *
 * Futuristic by design — add/remove options per surface without touching this
 * component. Opens ABOVE the anchor (composer sits at the bottom of the view).
 * Uses the mandated PortalMenu (never inline ``absolute`` positioning).
 *
 * @typedef {Object} PlusOption
 * @property {string} id
 * @property {string} label
 * @property {import('react').ComponentType<any>} [icon]   lucide icon
 * @property {'toggle'|'action'} type
 * @property {boolean} [checked]       (toggle) current state
 * @property {() => void} [onToggle]   (toggle) flip handler
 * @property {() => void} [onClick]    (action) invoke handler
 * @property {boolean} [disabled]
 */
import React, { useRef, useState, useCallback } from 'react';
import { Plus } from 'lucide-react';
import { PortalMenu } from './PortalMenu.jsx';

export default function ComposerPlusMenu({ options = [], disabled = false, testId = 'composer-plus' }) {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const items = Array.isArray(options) ? options.filter(Boolean) : [];
  if (items.length === 0) return null;

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Add to prompt"
        title="Add to prompt"
        data-testid={testId}
        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <Plus className="h-4 w-4" aria-hidden />
      </button>

      <PortalMenu anchorRef={anchorRef} open={open} onClose={close} align="top-left" width={208}>
        <ul className="py-1" role="menu">
          {items.map((opt) => {
            const Icon = opt.icon;
            if (opt.type === 'toggle') {
              return (
                <li key={opt.id} role="menuitemcheckbox" aria-checked={!!opt.checked}>
                  <button
                    type="button"
                    disabled={opt.disabled}
                    onClick={() => opt.onToggle?.()}
                    data-testid={`${testId}-${opt.id}`}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                  >
                    {Icon ? <Icon className="h-3.5 w-3.5 shrink-0 text-gray-400" aria-hidden /> : null}
                    <span className="flex-1 truncate">{opt.label}</span>
                    {/* Inline switch — mirrors the existing skills-toggle look. */}
                    <span className={`relative inline-flex h-3.5 w-6 shrink-0 items-center rounded-full transition-colors ${opt.checked ? 'bg-violet-600' : 'bg-gray-300'}`}>
                      <span className={`inline-block h-2.5 w-2.5 transform rounded-full bg-white transition-transform ${opt.checked ? 'translate-x-3' : 'translate-x-0.5'}`} />
                    </span>
                  </button>
                </li>
              );
            }
            return (
              <li key={opt.id} role="menuitem">
                <button
                  type="button"
                  disabled={opt.disabled}
                  onClick={() => { opt.onClick?.(); close(); }}
                  data-testid={`${testId}-${opt.id}`}
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                >
                  {Icon ? <Icon className="h-3.5 w-3.5 shrink-0 text-gray-400" aria-hidden /> : null}
                  <span className="flex-1 truncate">{opt.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </PortalMenu>
    </>
  );
}
